<button type="button" class="pbx-suggest-toggle" (click)="toggle()" [attr.aria-expanded]="isOpen">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
  {{ isOpen ? 'Hide PBX speakers' : 'Find speakers on PBX' }}
</button>

@if (isOpen) {
<div class="pbx-suggest-panel">
  @if (available === null) {
  <p class="pbx-suggest-note">Checking the PBX connection…</p>
  } @else if (!available) {
  <p class="pbx-suggest-note">No PBX is connected on the server, so speakers cannot be looked up. Ask IT to set the
    Jetwave or VitalPBX settings on the API and restart it.</p>
  } @else {
  <div class="pbx-suggest-top">
  <div class="pbx-suggest-search">
    <svg class="pbx-suggest-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
    <input type="text" class="pbx-suggest-input" placeholder="Site address, e.g. 5 Times Sq" [(ngModel)]="query"
      (keydown.enter)="$event.preventDefault(); search()" aria-label="Search speakers on the PBX" />
    <button type="button" class="pbx-suggest-search-btn" (click)="search()" [disabled]="searching">
      @if (searching) {
      <span class="spinner-border spinner-border-sm" role="status"></span>
      } @else {
      Search
      }
    </button>
  </div>
  <button type="button" class="pbx-suggest-close" (click)="toggle()" title="Close" aria-label="Close PBX speakers">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
      stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  </button>
  </div>

  @for (error of errors; track error) {
  <p class="pbx-suggest-note pbx-suggest-note--error">{{ error }}</p>
  }

  @if (searched && !results.length && !errors.length) {
  <p class="pbx-suggest-note">No PBX speaker name contains "{{ query }}". Try only the street number and name.</p>
  }

  @if (results.length) {
  <div class="pbx-suggest-summary">
    <span>
      <strong>{{ results.length }}</strong> {{ results.length === 1 ? 'speaker' : 'speakers' }}
      <!-- Only when some speaker has a real status: "0 online" for a VitalPBX-only list would
           read as all dead when the PBX simply cannot say. -->
      @if (hasKnownStatus) {
      <span class="pbx-suggest-sep">·</span>
      <span class="pbx-count pbx-count--online">{{ onlineCount }} online</span>
      }
      @if (offlineCount) {
      <span class="pbx-suggest-sep">·</span>
      <span class="pbx-count pbx-count--offline">{{ offlineCount }} offline</span>
      }
    </span>
    @if (addableOnline.length) {
    <button type="button" class="pbx-suggest-add-all" (click)="addAllOnline()">
      + Add all online ({{ addableOnline.length }})
    </button>
    }
  </div>

  @if (siteId && camerasLoaded && !cameras.length) {
  <p class="pbx-suggest-note pbx-suggest-note--bar">This site has no cameras yet (a camera is created by its first
    alert). Speakers are added without a camera.</p>
  }

  <ul class="pbx-suggest-list">
    @for (candidate of results; track candidate.provider + candidate.extension) {
    @let key = keyOf(candidate);
    <!-- 'unknown' is no status at all (VitalPBX has no registration API), so neither the
         dot nor the label is shown for it, and the card is not dimmed as if offline. -->
    @let hasStatus = candidate.status !== 'unknown';
    <li class="pbx-suggest-item" [class.is-offline]="hasStatus && candidate.status !== 'online'">
    <div class="pbx-suggest-row" [class.is-added]="isAdded(candidate)">
      @if (hasStatus) {
      <span class="pbx-dot pbx-dot--{{ candidate.status }}" [title]="statusLabels[candidate.status]"></span>
      }
      <span class="pbx-suggest-name" [title]="candidate.name">
        <span class="pbx-suggest-title">{{ shortName(candidate.name) }}</span>
        <span class="pbx-suggest-meta">
          <span class="pbx-provider pbx-provider--{{ candidate.provider }}">{{ providerLabels[candidate.provider] || candidate.provider }}</span>
          <span class="pbx-ext">{{ candidate.extension }}</span>
          @if (hasStatus) {
          <span class="pbx-status pbx-status--{{ candidate.status }}">{{ statusLabels[candidate.status] }}</span>
          }
          @if (candidate.model) {
          <span>{{ candidate.model }}</span>
          }
        </span>
      </span>
      @if (isAdded(candidate)) {
      <span class="pbx-suggest-added">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        Added
      </span>
      } @else {
      <button type="button" class="pbx-suggest-add" (click)="addCandidate(candidate)"
        [attr.aria-label]="'Add ' + candidate.name">+ Add</button>
      }
    </div>

    @if (siteId && (camerasLoading || cameras.length)) {
    <div class="pbx-suggest-camera">
      <div class="pbx-suggest-camera-head">
        <span class="pbx-suggest-camera-label">
          <svg class="pbx-suggest-camera-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m23 7-7 5 7 5V7z"></path>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
          Camera
        </span>
        @if (!camerasLoading && !isBoundElsewhere(candidate)) {
        <span class="pbx-match pbx-match--{{ cameraMatches[key] || 'none' }}">{{ matchLabels[cameraMatches[key] || 'none'] }}</span>
        }
      </div>
      @if (isBoundElsewhere(candidate)) {
      <span class="pbx-suggest-linked">Linked to speaker "{{ candidate.already_bound_to?.name }}" on another site</span>
      } @else {
      <!-- Panel wider than the card: camera names run long ("5 Times Sq | Camera 07"). -->
      <div class="pbx-suggest-camera-pick">
        <sw-select [options]="cameras" optionLabel="label" optionValue="_id" filterBy="label" [showClear]="true"
          placeholder="Select camera" [loading]="camerasLoading" appendTo="body" panelWidth="24rem"
          [ngModel]="cameraPicks[key]" (ngModelChange)="onCameraChange(candidate, $event)"></sw-select>
      </div>
      }
    </div>
    }
    </li>
    }
  </ul>
  }
  }
</div>
}
